import { useTranslate } from "@tolgee/react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { isAddress } from "viem";
import { WithdrawConfirmAlert } from "#/components/ui/withdraw-confirm-alert";
import { getHyperliquidErrorMessage } from "#/features/trade/lib/hyperliquid-error";
import { formatSize, toNumber } from "#/lib/formatters";
import { cn } from "#/lib/utils";
import {
	AvailableUsdcCard,
	formatMaxAmount,
	sanitizeAmountInput,
	truncateAddress,
	usePrefillMaxOnce,
} from "../shared-fund-flow";

export function StepExternalWithdraw({
	amount,
	destination,
	inflightTransfer,
	isPending,
	readOnly,
	transferPending,
	walletBalance,
	onAmountChange,
	onDestinationChange,
	onConfirm,
	onSuccess,
	setIsPending,
}: {
	amount: string;
	destination: string;
	/**
	 * The backend is still executing an earlier transfer (deposit or
	 * withdrawal). It permits only one per user, so submitting now would 409.
	 */
	inflightTransfer: boolean;
	isPending: boolean;
	readOnly: boolean;
	/** Step 1 → wallet transfer is still settling on-chain (~5 min). */
	transferPending: boolean;
	walletBalance: number;
	onAmountChange: (value: string) => void;
	onDestinationChange: (value: string) => void;
	onConfirm?: (payload: {
		amount: string;
		destination: `0x${string}`;
	}) => Promise<unknown>;
	onSuccess: (result: unknown) => void;
	setIsPending: (value: boolean) => void;
}) {
	const { t } = useTranslate();
	const [error, setError] = useState<string | null>(null);
	const [showConfirm, setShowConfirm] = useState(false);

	useEffect(() => {
		if (readOnly) setError(null);
	}, [readOnly]);

	const maxAmount = walletBalance > 0 ? formatMaxAmount(walletBalance) : "";
	usePrefillMaxOnce(!readOnly, maxAmount, amount, onAmountChange);

	function handleDestinationChange(nextValue: string) {
		onDestinationChange(nextValue.trim());
		setError(null);
	}

	function handleAmountChange(nextValue: string) {
		onAmountChange(sanitizeAmountInput(nextValue));
		setError(null);
	}

	function handleMax() {
		if (walletBalance <= 0 || readOnly) {
			onAmountChange("");
			return;
		}
		onAmountChange(maxAmount);
		setError(null);
	}

	const enteredAmount = toNumber(amount);
	const exceedsBalance = enteredAmount > walletBalance;
	const hasInvalidAddress = destination.length > 0 && !isAddress(destination);
	const canConfirm =
		!readOnly &&
		!inflightTransfer &&
		enteredAmount > 0 &&
		walletBalance > 0 &&
		enteredAmount <= walletBalance &&
		isAddress(destination);

	function handleWithdrawClick() {
		if (!canConfirm || isPending) return;
		setShowConfirm(true);
	}

	function executeWithdraw() {
		setShowConfirm(false);
		setIsPending(true);
		setError(null);
		const confirmation =
			onConfirm?.({
				amount,
				destination: destination as `0x${string}`,
			}) ?? Promise.resolve(undefined);

		confirmation
			.then((result) => onSuccess(result))
			.catch((err: unknown) =>
				setError(getHyperliquidErrorMessage(t, err, t("withdraw.failed"))),
			)
			.finally(() => setIsPending(false));
	}

	return (
		<div className="flex flex-col gap-3">
			{transferPending && (
				<div className="flex items-start gap-2 rounded-[12px] border border-primary/20 bg-primary/10 px-3.5 py-2.5 text-label2 text-primary">
					<CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{t("transferWallet.success-message")}</span>
				</div>
			)}

			{inflightTransfer && (
				<div className="flex items-start gap-2 rounded-[12px] border border-primary/20 bg-primary/10 px-3.5 py-2.5 text-label2 text-primary">
					<Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
					<span>{t("withdraw.reason.transfer-in-progress")}</span>
				</div>
			)}

			<AvailableUsdcCard
				label={t("withdraw.available")}
				amount={walletBalance}
			/>

			<div className="rounded-[16px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
				<div className="text-caption1-medium uppercase text-trade-text-faint">
					{t("withdraw.address")}
				</div>

				<input
					type="text"
					value={destination}
					onChange={(event) => handleDestinationChange(event.target.value)}
					placeholder={t("withdraw.address-placeholder")}
					disabled={isPending || readOnly}
					aria-label={t("withdraw.address")}
					className="mt-2 h-10 w-full min-w-0 rounded-xl border border-trade-border-subtle bg-trade-surface px-3 text-body2 text-trade-text-strong outline-none placeholder:text-trade-text-faint disabled:opacity-50"
				/>

				{hasInvalidAddress && (
					<div
						className={cn(
							"mt-2 min-h-5 text-right text-caption1-medium",
							hasInvalidAddress ? "text-primary" : "text-transparent",
						)}
					>
						{hasInvalidAddress ? t("withdraw.invalid-address") : "."}
					</div>
				)}
			</div>

			<div className="rounded-[16px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
				<div className="flex items-center justify-between gap-3 text-caption1-medium uppercase text-trade-text-faint">
					<span>{t("form.amount")}</span>
					<button
						type="button"
						onClick={handleMax}
						disabled={isPending || readOnly}
						className="inline-flex min-h-6 items-center rounded-xl border border-primary/20 bg-primary/10 px-2 text-caption1-medium text-primary transition-colors hover:bg-primary/16 disabled:opacity-50"
					>
						{t("form.max")}
					</button>
				</div>

				<div className="mt-2 flex items-center gap-3">
					<input
						type="text"
						inputMode="decimal"
						value={amount}
						onChange={(event) => handleAmountChange(event.target.value)}
						placeholder="0.0"
						disabled={isPending || readOnly}
						aria-label={t("withdraw.amount-aria")}
						className="h-10 w-full min-w-0 border-none bg-transparent px-0 text-title1 text-trade-text-strong outline-none placeholder:text-trade-text-faint disabled:opacity-50"
					/>
					<span className="shrink-0 text-label1-bold text-trade-text-faint">
						USDC
					</span>
				</div>

				<div
					className={cn(
						"mt-1 min-h-5 text-right text-caption1-medium",
						exceedsBalance && !transferPending
							? "text-primary"
							: "text-transparent",
					)}
				>
					{exceedsBalance && !transferPending
						? t("validation.exceeds-available", {
								balance: formatSize(walletBalance, 6),
							})
						: "."}
				</div>
			</div>

			{error && (
				<div className="flex items-start gap-2 rounded-[12px] border border-[var(--color-theme-status-negative)]/20 bg-[var(--color-theme-status-negative)]/10 px-3.5 py-2.5 text-label2 text-[var(--color-theme-status-negative)] dark:text-[var(--color-theme-status-negative)]">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			<button
				type="button"
				onClick={handleWithdrawClick}
				disabled={!canConfirm || isPending}
				className="mt-1 flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,transform,opacity] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 cursor-pointer"
			>
				{isPending ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin" />
						{t("actions.withdrawing")}
					</>
				) : inflightTransfer ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin" />
						{t("actions.withdraw")}
					</>
				) : transferPending && !canConfirm ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin" />
						{t("withdraw.awaiting-funds")}
					</>
				) : (
					t("actions.withdraw")
				)}
			</button>

			<WithdrawConfirmAlert
				open={showConfirm}
				onOpenChange={setShowConfirm}
				amount={`${amount} USDC`}
				address={truncateAddress(destination)}
				onConfirm={executeWithdraw}
			/>
		</div>
	);
}
